import type { ComponentProps, ReactNode } from "react";

import { t } from "../contract/strings";
import { cx } from "./cx";

export interface OrDividerProps extends Omit<ComponentProps<"div">, "children"> {
  /**
   * `doors` drops the standalone margin -- inside the join-door grid the gap supplies the
   * spacing -- and stands the rule on its side, where the doors sit in fixed columns at
   * every width.
   */
  placement?: "standalone" | "doors";
  /**
   * The word on the rule. Defaults to "or"; a screen may stamp its own register -- the
   * front door rules off its hero from its actions with "sign in". Uppercased by the
   * shared classes, so a screen reader still hears a word, not a spelling.
   */
  label?: ReactNode;
}

/**
 * Between two options, never inside one of them.
 *
 * As "Or enter the code" the word sat in the second option's own label, which quietly made
 * that option the afterthought; they are equal paths. Sign-in uses it between the magic
 * link and Google, the home screen between the two join doors.
 *
 * A word rather than a bare rule: two buttons stacked with a line between them look like a
 * sequence, and these are alternatives -- either one gets you in, and doing the second
 * after the first is not a step, it is a different door.
 *
 * Mono caps is this project's instrument-label register, the same one a field label uses.
 */
export function OrDivider({ placement = "standalone", label = t("common.or"), className, ...rest }: OrDividerProps) {
  return (
    <div
      className={cx(
        "flex items-center gap-stack text-ink-soft font-code text-label font-semibold uppercase tracking-label or-rule",
        placement === "standalone" ? "my-6" : "m-0 flex-col or-rule-vertical",
        className
      )}
      {...rest}
    >
      <span className="retrack-label">{label}</span>
    </div>
  );
}
